<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'

import type { ChallengeWheelSyncState, MiniGame, ThemeContent } from '~/stores/challengeWheel'

import ChallengeWheelSetupScreen from '~/components/ChallengeWheel/SetupScreen.vue'

definePageMeta({
  layout: 'games',
  prerender: true,
})

const { t } = useI18n()
const route = useRoute()
const store = useChallengeWheelStore()
const toast = useToast()
const { trackEvent } = useAnalytics()
const { open: openAbout } = useAboutModal()

const { data: footerContent } = await useAsyncData('component-footer', () =>
  queryCollection('componentFooter').first(),
)
const footerCreditParts = computed(() => {
  const line = footerContent.value?.credit.line ?? ''
  const [ before, after = '' ] = line.split('{kaweahTech}')
  return { after, before }
})

// Defer view-mode detection to post-mount so the server-rendered (controller)
// HTML and the initial client hydration tree always agree. Without this, loading
// the page with ?view=presenter would produce Vue hydration mismatch warnings
// because the prerendered HTML never contains query params.
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})

const viewMode = computed<'controller' | 'presenter'>(() => {
  if (!mounted.value) {
    return 'controller'
  }
  return route.query.view === 'presenter' ? 'presenter' : 'controller'
})
const isControllerView = computed(() => viewMode.value === 'controller')
const isPresenterView = computed(() => viewMode.value === 'presenter')

const {
  connected: presentationConnected,
  connecting: presentationConnecting,
  connectPresentationAsWindow,
  disconnectPeerPresenters,
  disconnectPresentation,
  lastError: presentationError,
  pairingCode,
  pairingUrl,
  peerPresentersCount,
  peerReady,
  popupPresenterConnected,
  presentationSupported,
  presenterEntryUrl,
  presenterLeave,
  presenterTransportKind,
  regeneratePairingCode,
} = useChallengeWheelPresentation(viewMode)

const router = useRouter()

// ── Setup screen ref (for header menu actions) ─────────
const setupRef = ref<InstanceType<typeof ChallengeWheelSetupScreen> | null>(null)

async function handleConnectPresentationWindow() {
  if (!presentationSupported.value) {
    toast.add({
      color: 'warning',
      description: ui.value?.setup.presentation.unsupported,
      duration: 2800,
      icon: 'mdi:alert-circle-outline',
    })
    return
  }

  const opened = await connectPresentationAsWindow()
  if (opened) {
    return
  }

  if (presentationError.value) {
    toast.add({
      color: 'error',
      description: ui.value?.setup.presentation.connectionFailed,
      duration: 3200,
      icon: 'mdi:alert-circle-outline',
    })
    return
  }

  toast.add({
    color: 'error',
    description: ui.value?.setup.presentation.connectionFailed,
    duration: 3200,
    icon: 'mdi:alert-circle-outline',
  })
}

function handleDisconnectPeerPresenters() {
  disconnectPeerPresenters()
  toast.add({
    color: 'neutral',
    description: ui.value?.setup.presentation.peerDisconnected,
    duration: 2200,
    icon: 'mdi:account-off-outline',
  })
}

function handleDisconnectPresentation() {
  disconnectPresentation()
}

function handleLaunchDisplayMode() {
  router.push({ query: { ...route.query, view: 'presenter' } })
}

function handlePresenterLeave() {
  presenterLeave()
}

function handleRegeneratePairingCode() {
  regeneratePairingCode()
  toast.add({
    color: 'neutral',
    description: ui.value?.setup.presentation.pairingRegenerated,
    duration: 2000,
    icon: 'mdi:refresh',
  })
}

// ── Header menu actions ────────────────────────────────
function handleReset() {
  setupRef.value?.handleReset()
}

async function handleShare() {
  const url = window.location.href
  if (navigator.share) {
    try {
      await navigator.share({ title: document.title, url })
    } catch {
      // user cancelled or browser blocked — fall through silently
    }
  } else {
    await navigator.clipboard.writeText(url)
    toast.add({ color: 'success', description: t('setupMenu.linkCopied'), duration: 2000, icon: 'mdi:check-circle' })
  }
}

// ── Load all content at once ──────────────────────────────
const [ { data: allContent }, { data: ui } ] = await Promise.all([
  useAsyncData(
    'challenge-wheel-content',
    () => queryCollection('challengeWheel').all(),
  ),
  useChallengeWheelUI(),
])

watch(presentationConnected, (isNowConnected, wasConnected) => {
  if (!isControllerView.value || !wasConnected || isNowConnected) {
    return
  }
  toast.add({
    color: 'warning',
    description: ui.value?.setup.presentation.disconnectedStatus,
    duration: 2500,
    icon: 'mdi:monitor-off',
  })
})

useSeoMeta({
  description: ui.value?.page.description,
  ogDescription: ui.value?.page.description,
  title: ui.value?.page.title,
})

function getThemeContent(): null | ThemeContent {
  const found = allContent.value?.find(c => c.theme === store.config.theme)
  return found as null | ThemeContent
}

// ── URL-driven game phase resume ──────────────────────────
// The setup ↔ playing transition is reflected in `?phase=playing` so the
// browser back button returns to the setup landing, while a forward (or a
// reload while the URL still has `?phase=playing`) restores the in-progress
// game from localStorage. A bare URL is always the setup landing — even if
// localStorage has stale game state from a previous session — so a fresh
// visit doesn't accidentally drop the leader into the middle of a game.
const persistedSyncState = useLocalStorage<ChallengeWheelSyncState | null>(
  'sfg.challenge-wheel.gameState',
  null,
  { serializer: { read: v => v ? JSON.parse(v) : null, write: v => JSON.stringify(v) } },
)

function restoreFromPersistedIfAny() {
  if (!isControllerView.value) {
    return
  }
  if (route.query.phase === 'playing' && persistedSyncState.value && store.phase === 'setup') {
    store.applySyncState(persistedSyncState.value)
  }
}

if (import.meta.client) {
  // Persist state changes to localStorage while a game is in progress. Skip
  // when phase is `setup` so navigating back doesn't wipe the snapshot the
  // forward navigation needs to restore.
  watch(() => store.getSyncState(), state => {
    if (isControllerView.value && state.phase !== 'setup') {
      persistedSyncState.value = state
    }
  }, { deep: true })

  // Watch URL phase: forward to playing restores; back to bare resets.
  watch(() => route.query.phase, q => {
    if (!isControllerView.value) {
      return
    }
    if (q === 'playing') {
      if (persistedSyncState.value && store.phase === 'setup') {
        store.applySyncState(persistedSyncState.value)
      }
    } else if (store.phase !== 'setup') {
      store.exitGame()
    }
  })

  onMounted(() => {
    restoreFromPersistedIfAny()
  })
}

// ── Game reset hook ────────────────────────────────────
const { register: registerReset } = useGameReset()
registerReset(() => {
  if (import.meta.client && document.fullscreenElement) {
    document.exitFullscreen().catch(() => {})
  }
  store.exitGame()
  // Drop the phase query so the URL reflects the setup landing.
  if (route.query.phase) {
    void router.replace({ query: { ...route.query, phase: undefined } })
  }
})

// ── Game lifecycle ───────────────────────────────────────
function handleStart() {
  if (!isControllerView.value) {
    return
  }
  const content = getThemeContent()
  if (content) {
    store.setContent(content)
  }
  // Inject mini-game labels from content before the wheel is built.
  if (ui.value) {
    const labels = Object.fromEntries(
      ui.value.setup.miniGames.games.map(g => [ g.value, g.label ]),
    ) as Record<MiniGame, string>
    store.setMiniGameLabels(labels)
  }
  // Must request fullscreen synchronously within the user-gesture context.
  // Skip in dev mode so the browser toolbar stays accessible during development.
  // When a presentation window is connected, keep the controller as a normal
  // window so the leader can operate it while the presenter fills the screen.
  if (isControllerView.value && !presentationConnected.value && import.meta.client && !import.meta.dev) {
    document.documentElement.requestFullscreen().catch(() => {})
  }
  store.startGame()
  // Push history so back returns to setup; forward (or reload) resumes.
  void router.push({ query: { ...route.query, phase: 'playing' } })
  trackEvent('game_started', {
    game: 'showdown',
    selected_games: store.config.selectedGames,
    team_count: store.teams.length,
    team_structure: store.config.teamStructure,
    theme: store.config.theme,
  })
}
</script>

<template>
  <div
    v-if="isPresenterView"
    class="h-screen w-screen bg-default text-default"
  >
    <ChallengeWheelPresenterPairing v-if="presenterTransportKind === 'unpaired'"/>

    <ChallengeWheelGameScreen
      v-else-if="store.phase !== 'setup'"
      :interactive="false"
      :show-answers-by-default="false"
    />
    <div
      v-else
      class="flex h-full items-center justify-center px-6"
    >
      <div class="w-full max-w-3xl">
        <UCard :ui="{ body: 'bg-muted/5 text-center' }">
          <div class="flex flex-col items-center gap-5 py-6 sm:py-8">
            <UIcon
              class="size-14 text-primary"
              :name="presentationConnected ? 'mdi:check-circle-outline' : 'mdi:progress-clock'"
            />
            <h1 class="text-balance text-3xl font-black tracking-tight sm:text-4xl">
              {{ presentationConnected ? ui?.setup.presentation.presenterWaitingTitle : ui?.setup.presentation.presenterConnectingTitle }}
            </h1>
            <p class="max-w-2xl text-balance text-base text-muted sm:text-lg">
              {{ presentationConnected ? ui?.setup.presentation.presenterWaitingDescription : ui?.setup.presentation.presenterConnectingDescription }}
            </p>
          </div>
        </UCard>
      </div>
    </div>

    <ChallengeWheelPresenterFullscreenOverlay v-if="presenterTransportKind === 'peer' && presentationConnected"/>

    <!-- Exit / Close button — popup mode says "Close" (closes the window so
         the controller's poll detects it); peer mode says "Exit" (returns
         this tab to the pairing entry screen).

         While the GameScreen is rendering (dark bg, gameplay surface) the
         button uses the subtle in-game corner style to stay out of the
         room's way. On the lighter pairing / waiting screens it gets the
         full SfgChromeButton chrome since visibility there is fine. -->
    <SfgGameCornerButton
      v-if="presenterTransportKind !== 'unpaired' && store.phase !== 'setup'"
      class="fixed top-4 left-4 z-40"
      :icon="presenterTransportKind === 'broadcast' ? 'mdi:close' : 'mdi:arrow-left'"
      :aria-label="presenterTransportKind === 'broadcast' ? ui?.setup.presentation.presenterCloseButton ?? 'Close' : ui?.setup.presentation.presenterLeaveButton ?? 'Exit'"
      @click="handlePresenterLeave"
    />
    <SfgChromeButton
      v-else
      class="fixed top-4 left-4 z-40"
      :icon-left="presenterTransportKind === 'broadcast' ? 'mdi:close' : 'mdi:arrow-left'"
      @click="handlePresenterLeave"
    >
      {{ presenterTransportKind === 'broadcast' ? ui?.setup.presentation.presenterCloseButton : ui?.setup.presentation.presenterLeaveButton }}
    </SfgChromeButton>
  </div>

  <!-- Setup screen -->
  <div
    v-else-if="store.phase === 'setup'"
    class="flex min-h-screen flex-col items-center justify-start p-6 xl:justify-center"
  >
    <SfgChromeButton
      to="/games"
      icon-left="mdi:arrow-left"
      class="self-start mb-4 z-30 xl:fixed xl:top-4 xl:left-4 xl:mb-0"
    >
      {{ t('setupMenu.backToLibrary') }}
    </SfgChromeButton>
    <div class="w-full max-w-3xl">
      <UCard :ui="{ root: 'shadow-xl ring', header: 'bg-muted/75', body: 'bg-muted/5', footer: 'bg-muted/20' }">
        <!-- Card header: icon + title/description + corner menu button -->
        <template #header>
          <div class="flex items-start gap-4 py-1">
            <UIcon
              class="size-12 shrink-0"
              name="twemoji:crossed-swords"
            />
            <div class="min-w-0 flex-1">
              <h1 class="text-2xl font-bold leading-tight">
                {{ ui?.page.title }}
              </h1>
              <p class="mt-1 text-sm text-muted">
                {{ ui?.page.description }}
              </p>
            </div>
            <UDropdownMenu
              :items="[
                { icon: 'mdi:share-variant-outline', label: t('setupMenu.shareGame'), onSelect: handleShare },
                { icon: 'mdi:refresh', label: t('setupMenu.resetSettings'), onSelect: handleReset },
                { icon: 'mdi:information-outline', label: t('setupMenu.about'), onSelect: () => openAbout() },
                { icon: 'mdi:arrow-left', label: t('setupMenu.backToLibrary'), onSelect: () => router.push('/games') },
              ]"
              :content="{ side: 'bottom', align: 'end', sideOffset: 8 }"
              :modal="false"
              :ui="{ content: 'w-48' }"
            >
              <SfgChromeButton
                square
                icon-left="mdi:dots-vertical"
                :aria-label="t('setupMenu.menuAriaLabel')"
                class="shrink-0"
              />
            </UDropdownMenu>
          </div>
        </template>

        <template #footer>
          <div v-if="footerContent" class="w-full flex flex-col items-center gap-3 text-center">
            <NuxtLink to="/" class="inline-flex items-center gap-3 group">
              <SfgFlame
                :size="32"
                :disk="false"
                body-color="red"
                flame-color="yellow"
                :tilt="1"
              />
              <div class="flex flex-col leading-tight text-left">
                <span class="font-display uppercase text-[12px] tracking-[0.08em] text-default group-hover:text-primary transition-colors">
                  {{ footerContent.wordmark.label }}
                </span>
                <span class="font-body text-[10px] text-muted">
                  {{ footerContent.wordmark.sublabel }}
                </span>
              </div>
            </NuxtLink>
            <p class="font-body text-[11px] text-muted">
              {{ footerContent.credit.year }} / {{ footerCreditParts.before }}<a
                :href="footerContent.credit.kaweahTechUrl"
                target="_blank"
                rel="noopener"
                class="text-inherit no-underline hover:underline"
              >Kaweah Tech</a>{{ footerCreditParts.after }}
            </p>
          </div>
        </template>

        <!-- Setup form -->
        <ClientOnly>
          <ChallengeWheelSetupScreen
            ref="setupRef"
            :pairing-code="pairingCode"
            :pairing-url="pairingUrl"
            :peer-presenters-count="peerPresentersCount"
            :peer-ready="peerReady"
            :popup-presenter-connected="popupPresenterConnected"
            :presentation-connecting="presentationConnecting"
            :presentation-supported="presentationSupported"
            :presenter-entry-url="presenterEntryUrl"
            @start="handleStart"
            @connect-presentation-window="handleConnectPresentationWindow"
            @disconnect-peer-presenters="handleDisconnectPeerPresenters"
            @disconnect-presentation="handleDisconnectPresentation"
            @launch-display-mode="handleLaunchDisplayMode"
            @regenerate-pairing-code="handleRegeneratePairingCode"
          />
          <template #fallback>
            <div class="flex flex-col items-center gap-4 py-16 text-center">
              <UIcon
                class="size-12 text-primary animate-pulse"
                name="mdi:progress-clock"
              />
              <h2 class="text-balance text-2xl font-black tracking-tight">
                {{ ui?.setup.loading }}
              </h2>
            </div>
          </template>
        </ClientOnly>
      </UCard>
    </div>
  </div>

  <!-- Game screen — fullscreen, no site chrome -->
  <ChallengeWheelGameScreen
    v-else
    :interactive="true"
    :presentation-connected="presentationConnected"
    :show-answers-by-default="presentationConnected"
  />

  <!-- In-game FAB: only when actively playing, never in setup -->
  <GameFab v-if="store.phase !== 'setup'"/>
</template>
